import {distinctUntilChanged, filter, fromEvent, map, merge, Observable, Subject, takeUntil, tap} from "rxjs";
import {singleton} from "tsyringe";
import {DraggingMode} from "../enums/dragging-mode.enum";

export type PointerState = {
    pressed: boolean,
    coordinates: {
        x: number,
        y: number
    }
}

type RegisteredElementMetadata = {
    threshold: number,
    x: number,
    y: number
};

export type DraggingEventMessage<T = any> = {
    event: string
    payload?: T | null
}

@singleton()
export class TimelineMovingDraggingService {

    private _disconnect$ = new Subject<void>();

    private _pipeline = new Subject<DraggingEventMessage>();

    private _registeredElements = new Map<HTMLElement, RegisteredElementMetadata>();

    private _remainder = {
        x: 0,
        y: 0
    };

    private _stopListenToPointerMove$ = new Subject<void>();
    private _mode: DraggingMode | null = null;

    public listen<T = any>(event: RegExp | null = null): Observable<DraggingEventMessage<T>> {
        if (event === null) {
            return this._pipeline;
        }

        return this._pipeline.pipe(
            takeUntil(this._disconnect$),
            filter(message => event.test(message.event))
        );
    }

    public register(element: HTMLElement, threshold: number = 35): void {

        this._registeredElements.set(element, {
            threshold,
            x: 0,
            y: 0
        });
        element.addEventListener(`contextmenu`, evt => evt.preventDefault());

        /*
         * Pointer Events
         */
        const _pointerDown$ = fromEvent<PointerEvent>(element, 'pointerdown')
            .pipe(
                takeUntil(this._disconnect$),
                filter(evt => evt.button === 2),
                tap(value => this._mode = DraggingMode.POINTER),
                map<PointerEvent, PointerState>(evt => ({
                    pressed: true,
                    coordinates: {
                        x: evt.clientX,
                        y: evt.clientY
                    }
                }))
            );

        const _pointerUp$ = fromEvent<PointerEvent>(element, 'pointerup')
            .pipe(
                takeUntil(this._disconnect$),
                filter(evt => evt.button === 2 && this._mode === DraggingMode.POINTER),
                map<PointerEvent, PointerState>(evt => ({
                    pressed: false,
                    coordinates: {
                        x: evt.clientX,
                        y: evt.clientY
                    }
                }))
            );

        const _pointerOut$ = merge(
            fromEvent<PointerEvent>(document, 'pointerleave'),
            fromEvent<PointerEvent>(document, 'pointercancel'),
        ).pipe(
            takeUntil(this._disconnect$),
            filter(() => this._mode === DraggingMode.POINTER),
            tap(() => this._mode === null),
            map<PointerEvent, PointerState>(evt => ({
                pressed: false,
                coordinates: {
                    x: evt.clientX,
                    y: evt.clientY
                }
            }))
        );

        const _pointerActive$ = merge(
            _pointerDown$,
            _pointerUp$,
            _pointerOut$,
        ).pipe(
            takeUntil(this._disconnect$),
            distinctUntilChanged<PointerState>((prev, curr) => prev.pressed === curr.pressed),
            tap<PointerState>(state => {
                this._pipeline.next({
                    event: state.pressed ? 'pointer-pressed' : 'pointer-released',
                    payload: state.coordinates
                });
            }),
        );

        _pointerActive$.subscribe(pointerState => {
            const metaData = this._registeredElements.get(element)!;
            if (pointerState.pressed) {
                metaData.x = pointerState.coordinates.x;
                metaData.y = pointerState.coordinates.y;
                // Reset Y remainder when starting new drag session (X remainder preserved for visual feedback)
                this._remainder.y = 0;
            }

            this._pipeline.next({
                event: pointerState.pressed ? 'drag-mode.enabled' : 'drag-mode.disabled',
                payload: pointerState.coordinates
            });

            if (pointerState.pressed) {
                this._startListenToPointerOrTouchMove(element);
            } else {
                this._stopListenToPointerMove();
            }
        });
    }

    public unregister(): void {
        this._disconnect$.next();
        this._registeredElements.clear();
    }

    private _calculateStepsWithRemainder(coordinate: number, existingRemainder: number, threshold: number): {
        numberOfSteps: number,
        remainder: number
    } {
        const delta = coordinate + existingRemainder;
        const numberOfSteps = Math.floor(delta / threshold);
        const calculatedRemainder = delta - numberOfSteps * threshold;


        return {
            numberOfSteps: numberOfSteps,
            remainder: calculatedRemainder
        }
    }

    private _startListenToPointerOrTouchMove(element: HTMLElement): void {
        merge(
            fromEvent<PointerEvent>(element, 'pointermove')
                .pipe(
                    takeUntil(this._disconnect$),
                    takeUntil(this._stopListenToPointerMove$),
                    filter(value => this._mode === DraggingMode.POINTER),
                    map(event => {
                        if (!this._registeredElements.has(element)) {
                            throw new Error(`There are no dragging listener for element registered`);
                        }

                        const metaData = this._registeredElements.get(element)!;
                        const coordsDelta = {
                            emit: true,
                            x: event.clientX - metaData.x,
                            y: event.clientY - metaData.y,
                            remainder: {
                                x: 0,
                                y: 0
                            }
                        };

                        metaData.x = event.clientX;
                        const calculationForX = this._calculateStepsWithRemainder(coordsDelta.x, this._remainder.x, metaData.threshold);
                        this._remainder.x = coordsDelta.remainder.x = calculationForX.remainder;
                        coordsDelta.x = calculationForX.numberOfSteps;

                        // Y-axis calculation without pre-inversion
                        const calculationForY = this._calculateStepsWithRemainder(coordsDelta.y, this._remainder.y, metaData.threshold);

                        this._remainder.y = calculationForY.remainder;
                        coordsDelta.remainder.y = calculationForY.remainder;
                        coordsDelta.y = -calculationForY.numberOfSteps; // Negate after calculation

                        // Update metaData.y to current position for next delta calculation
                        metaData.y = event.clientY;

                        return coordsDelta;
                    }),
                    filter(coords => coords.emit),
                    map(coords => ({x: coords.x, y: coords.y, remainder: coords.remainder})),
                )
        )
            .subscribe(coords => {
                this._pipeline.next({
                    event: `moved`,
                    payload: coords
                })
            });
    }

    private _stopListenToPointerMove() {
        this._stopListenToPointerMove$.next();
        // Reset only Y remainder (X remainder preserved for visual feedback)
        this._remainder.y = 0;
    }
}